import React, { useState, useLayoutEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SubtaskEditor, SubtaskChecklist } from '@/components/shared/Subtasks';
import { RecurrenceOptions } from '@/components/planner/views/PlannerDialogs';
import { recurrencePayloadFields, recurrenceStateFromRow, describeRecurrence } from '@/lib/recurrenceState';
import { toast } from 'sonner';
import { Repeat } from 'lucide-react';
import { format, parseISO } from 'date-fns';

/**
 * Edit dialog for shared tasks — saves via useSharedItems.updateSharedItem.
 * Creator: edits everything. Assignee: read-only details + tickable subtasks.
 */
export function SharedTaskEditDialog({ open, onOpenChange, task, members = [], currentUserId, onSave }) {
  const [form, setForm] = useState({ title: '', description: '', due_date: '', priority_level: 'none', assigned_to: '', subtasks: [], isRecurring: false, ...recurrenceStateFromRow(null) });
  const [saving, setSaving] = useState(false);

  const isCreator = !!task && (!currentUserId || task.created_by === currentUserId);

  // Layout effect, like the Add dialog: seeding must land before the first
  // paint, or opening task B briefly shows task A's form.
  useLayoutEffect(() => {
    if (task) {
      setForm({
        title: task.title || '',
        description: task.description || '',
        due_date: task.due_date || '',
        priority_level: task.priority_level || 'none',
        assigned_to: task.assigned_to || '',
        subtasks: (task.subtasks || []).map((s) => ({ ...s })),
        isRecurring: !!task.is_recurring,
        ...recurrenceStateFromRow(task),
      });
    }
  }, [task]);

  const setSubtasks = (subtasks) => setForm((f) => ({ ...f, subtasks }));

  const tickSubtask = async (subId, checked) => {
    const next = form.subtasks.map((s) => (s.id === subId ? { ...s, completed: checked } : s));
    setSubtasks(next);
    if (!isCreator) {
      // Assignee ticks save instantly (subtasks-only PATCH allowed by backend)
      try {
        await onSave(task.id, { subtasks: next });
      } catch (e) {
        setSubtasks(form.subtasks);
        toast.error(e.message || "Couldn't update the checklist");
      }
    }
  };

  const submit = async () => {
    if (isCreator && !form.title.trim()) { toast.error('Title is required'); return; }
    // The server refuses a repeating task with no due date (it needs an anchor
    // to spawn the next occurrence from) — say so before the round trip.
    if (isCreator && form.isRecurring && !form.due_date) {
      toast.error('A repeating task needs a due date');
      return;
    }
    setSaving(true);
    try {
      let payload;
      if (isCreator) {
        // Only send changed fields — avoids spurious "updated" notifications
        payload = {};
        if (form.title.trim() !== (task.title || '')) payload.title = form.title.trim();
        if (form.description.trim() !== (task.description || '')) payload.description = form.description.trim();
        if ((form.due_date || '') !== (task.due_date || '')) payload.due_date = form.due_date || '';
        if (form.priority_level !== (task.priority_level || 'none')) payload.priority_level = form.priority_level;
        if (form.assigned_to && form.assigned_to !== task.assigned_to) payload.assigned_to = form.assigned_to;
        if (JSON.stringify(form.subtasks) !== JSON.stringify(task.subtasks || [])) payload.subtasks = form.subtasks;
        // Recurrence: compare the rule as the server would store it, so
        // opening and saving the dialog without touching Repeat sends nothing
        // (the backend rejects a rule change from anyone but the creator).
        const ruleOf = (recurring, src) => JSON.stringify({ r: !!recurring, ...(recurring ? recurrencePayloadFields(src) : {}) });
        const before = ruleOf(task.is_recurring, recurrenceStateFromRow(task));
        const after = ruleOf(form.isRecurring, form);
        if (before !== after) {
          payload.is_recurring = !!form.isRecurring;
          if (form.isRecurring) Object.assign(payload, recurrencePayloadFields(form));
        }
        if (Object.keys(payload).length === 0) { onOpenChange(false); setSaving(false); return; }
      } else {
        payload = { subtasks: form.subtasks };
      }
      await onSave(task.id, payload);
      toast.success('Shared task updated');
      onOpenChange(false);
    } catch (e) {
      toast.error(e.message || "Couldn't update the task");
    } finally {
      setSaving(false);
    }
  };

  if (!task) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="shared-task-edit-dialog">
        <DialogHeader>
          <DialogTitle>{isCreator ? 'Edit Shared Task' : 'Shared Task'}</DialogTitle>
          <DialogDescription className="sr-only">
            {isCreator ? 'Edit the shared task details and checklist' : 'View task details and tick off your checklist'}
          </DialogDescription>
        </DialogHeader>
        {isCreator ? (
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-medium text-slate-500 dark:text-slate-400">Title</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="mt-1"
                data-testid="shared-edit-title"
              />
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-500 dark:text-slate-400">Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Add details for the assignee…"
                className="mt-1 min-h-[64px]"
                data-testid="shared-edit-description"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-medium text-slate-500 dark:text-slate-400">Due date</Label>
                <Input
                  type="date"
                  value={form.due_date}
                  onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                  className="mt-1"
                  data-testid="shared-edit-due"
                />
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-500 dark:text-slate-400">Priority</Label>
                <Select value={form.priority_level} onValueChange={(v) => setForm({ ...form, priority_level: v })}>
                  <SelectTrigger className="mt-1" data-testid="shared-edit-priority">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {members.length > 0 && (
              <div>
                <Label className="text-xs font-medium text-slate-500 dark:text-slate-400">Assigned to</Label>
                <Select value={form.assigned_to} onValueChange={(v) => setForm({ ...form, assigned_to: v })}>
                  <SelectTrigger className="mt-1" data-testid="shared-edit-assignee">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {members.map((m) => (
                      <SelectItem key={m.user_id} value={m.user_id}>{m.user_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div data-testid="shared-edit-subtasks">
              <SubtaskEditor
                items={form.subtasks.map((s) => ({ id: s.id, title: s.title }))}
                onChange={(items) =>
                  setSubtasks(items.map((i) => ({
                    id: i.id,
                    title: i.title,
                    completed: form.subtasks.find((p) => p.id === i.id)?.completed || false,
                  })))
                }
                checkedIds={form.subtasks.filter((s) => s.completed).map((s) => s.id)}
                onToggleCheck={tickSubtask}
                showChecks
              />
            </div>
            <div className="border border-slate-200 dark:border-white/10 rounded-xl overflow-hidden" data-testid="shared-edit-recurrence">
              <RecurrenceOptions data={form} setData={setForm} bare />
              {task.occurrence_number > 1 && (
                <p className="px-3 pb-3 text-xs text-slate-400 dark:text-slate-500" data-testid="shared-edit-occurrence-hint">
                  Occurrence {task.occurrence_number} of this series — an end count is measured from the first one.
                </p>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="shared-edit-cancel">Cancel</Button>
              <Button
                className="bg-violet-500 hover:bg-violet-600"
                onClick={submit}
                disabled={saving}
                data-testid="shared-edit-save"
              >
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3" data-testid="shared-task-assignee-view">
            <div className="text-sm font-medium text-slate-800 dark:text-slate-100">{task.title}</div>
            <div className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-3">
              <span>from {task.created_by_name}</span>
              {task.due_date && <span>due {format(parseISO(task.due_date), 'MMM d')}</span>}
              {task.priority_level && task.priority_level !== 'none' && <span className="capitalize">{task.priority_level} priority</span>}
            </div>
            {task.is_recurring && (
              <div className="text-xs text-slate-500 dark:text-slate-400 inline-flex items-center gap-1" data-testid="shared-task-repeats">
                <Repeat className="w-3 h-3" />{describeRecurrence(task)}
              </div>
            )}
            {task.description && (
              <p className="text-xs text-slate-600 dark:text-slate-300 whitespace-pre-wrap">{task.description}</p>
            )}
            {form.subtasks.length > 0 ? (
              <SubtaskChecklist
                className="!ml-0 !mr-0"
                items={form.subtasks.map((s) => ({ id: s.id, title: s.title }))}
                checkedIds={form.subtasks.filter((s) => s.completed).map((s) => s.id)}
                onToggle={tickSubtask}
              />
            ) : (
              <p className="text-xs text-slate-400 dark:text-slate-500">No checklist on this task.</p>
            )}
            <div className="flex justify-end pt-1">
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="shared-edit-close">Close</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default SharedTaskEditDialog;
